// src/pages/MainTable.tsx
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Styles/MainTable.css';

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, type MenuProps } from 'antd';

import { useAuth } from '../AuthContext';
import SearchBox from '../Components/MainTable/SearchBox';
import ViewToggle from '../Components/MainTable/ViewToggle';
import ProjectsTable from '../Components/MainTable/ProjectsTable';
import PlusIcon from '../Components/Icons/PlusIcon';

import { getUsers } from '../services/users';
import { getProjects } from '../services/projects';
import type { ProjectGroup } from '../types/project';
import type { User } from '../types/user';
import { ACCOUNT_TYPE } from 'src/constants/authConstants';
import TourGuide from 'src/Components/MainTable/TourGuide';

type View = 'all' | 'user';
const internalDomains = [
  'modularclosets.com',
  'alcoveclosets.com',
  'hexacoder.com',
  'wtioutsourcing.com',
];

const MainTable: React.FC = () => {
  const { user } = useAuth();
  const isTester = ['amenexmk@gmail.com', 'blagoja.koneski@mkmage.com'].includes(user.email);
  const isExternalUser =
    user.role === 'user-external' ||
    !internalDomains.some((domain) => user.email.toLowerCase().endsWith(`@${domain}`));

  const allowedViews: View[] = isTester || isExternalUser ? ['user'] : ['user', 'all'];

  const [view, setView] = useState<View>(allowedViews[0]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Quoted' | 'Draft'>('All');
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<ProjectGroup[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [selectedOwner, setSelectedOwner] = useState<string>('All');
  const [isTourOpen, setIsTourOpen] = useState<boolean>(false);

  const navigate = useNavigate();

  const newProjectMenuItems: MenuProps['items'] = [
    {
      key: 'blueprints',
      label: 'I have Blueprints',
      onClick: () => navigate('/add-project?type=blueprint'),
    },
    {
      key: 'design',
      label: 'Design my Space',
      onClick: () => navigate('/add-project?type=design'),
    },
  ];

  const showOwnerSelect =
    (view === 'all' && user.accountType === ACCOUNT_TYPE.MULTIFAMILY) || user.isDev;

  useEffect(() => {
    getUsers().then(setUsers);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    getProjects(view === 'all')
      .then((data) => {
        // Do not override API-chosen active. Trust `project.active` or `parentProject.activeProjectId`.
        setProjects([...(data || [])]);
        const uniqueOwners = new Set<string>();
        data.forEach((group) => {
          group.parentProject?.owners.forEach((owner) => uniqueOwners.add(owner));
        });
        setOwners(Array.from(uniqueOwners).sort());
      })
      .catch((err) => setError(err.message || 'Fetch failed'))
      .finally(() => setLoading(false));
  }, [view]);

  if (loading) {
    return (
      <div className='text-center py-5'>
        <span>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className='text-center py-5'>
        <span>Error: {error}</span>
      </div>
    );
  }

  return (
    <>
      <div className='container py-4 main-table-page'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <h1 className='heading'>Projects</h1>
          <div className='d-flex search-button-wrapper'>
            <SearchBox value={searchText} onChange={setSearchText} />
            <Dropdown
              menu={{ items: newProjectMenuItems }}
              trigger={['click']}
              className='add-new-project'
            >
              <a className='simple-button'>
                <PlusIcon />
                New Project
              </a>
            </Dropdown>
          </div>
        </div>

        <div className='filter-wrapper d-flex justify-content-between mb-4'>
          <div className='d-flex align-items-center gap-2'>
            <ViewToggle view={view} onChange={setView} allowed={allowedViews} />
            {view == 'all' && showOwnerSelect && (
              <div className='owner-select-wrapper'>
                <select
                  className='form-select owner-select'
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                >
                  <option value='All'>All Owners</option>
                  {owners.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <ProjectsTable
          view={view}
          searchText={searchText}
          statusFilter={statusFilter}
          users={users}
          projects={projects}
          selectedOwner={showOwnerSelect ? selectedOwner : undefined}
        />
      </div>
      <TourGuide isTourOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
    </>
  );
};

export default MainTable;
